import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WestRoundedIcon from '@mui/icons-material/WestRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CommonButtonActionButton from '../common/CommonButtonActionButton';
import httpcommon from '../../helper/httpcommon';
import { useToast } from '../../hook/useToast';
import AuthContext from '../../context/AuthContext';

const NAVY = '#292D5B';
const ORANGE = '#F6A03A';

const MotionBox = motion(Box);

/**
 * "Help Us Know You" step. Questions are fetched from the API (all at once,
 * like the parent/teacher feedback tab). Each question is either `checkbox`
 * (multi-select, value is an array) or `radio` (single-select, value is a
 * string). Answers are saved to a dedicated table via the
 * `help_us_know_you` API (independent of the conversational General test).
 *
 * Design: an immersive, full-width wizard that shows ONE question at a time.
 * The data model, validation and submit payload are unchanged.
 */
const defaultFor = (question) => (question.type === 'checkbox' ? [] : '');

const isAnswered = (question, value) =>
  question.type === 'checkbox'
    ? Array.isArray(value) && value.length > 0
    : typeof value === 'string' && value.trim() !== '';

const toTitleCase = (str) =>
  str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const OptionRow = ({ selected, label, onClick, multi }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2,
      py: 1.85,
      borderRadius: '14px',
      cursor: 'pointer',
      border: '1.5px solid',
      borderColor: selected ? ORANGE : '#E7EAF1',
      backgroundColor: selected ? '#FFF9F0' : '#fff',
      transition: 'all .15s ease',
      '&:hover': {
        borderColor: selected ? ORANGE : '#CFD4E2',
        backgroundColor: selected ? '#FFF9F0' : '#FBFBFE',
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        borderRadius: multi ? '8px' : '50%',
        border: '2px solid',
        borderColor: selected ? ORANGE : '#C4C9D6',
        backgroundColor: selected ? ORANGE : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all .15s ease',
      }}
    >
      {selected && <CheckRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />}
    </Box>
    <Typography sx={{ fontSize: 15, color: '#2C3350', fontWeight: selected ? 600 : 500, lineHeight: 1.45 }}>
      {label}
    </Typography>
  </Box>
);

const GeneralQuestions = ({ onComplete }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useContext(AuthContext);
  const userId = user?.userInfo?.user;

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [questionErrors, setQuestionErrors] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fetchingQuestions, setFetchingQuestions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  // Fetch questions, then prefill with any previously saved answers.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setFetchingQuestions(true);

        const questionRes = await httpcommon.get('/school/help_us_know_you/questions/');
        const fetched = Array.isArray(questionRes?.data) ? questionRes.data : [];
        if (cancelled) return;

        setQuestions(fetched);

        // Defaults per question type
        const defaults = {};
        fetched.forEach((q) => { defaults[q.qid] = defaultFor(q); });

        // Prefill from prior submission (404 = none yet, ignore)
        if (userId) {
          try {
            const answerRes = await httpcommon.get(`/school/help_us_know_you/submission/${userId}/`);
            const responses = answerRes?.data?.responses;
            if (Array.isArray(responses)) {
              responses.forEach(({ qid, answer }) => {
                const q = fetched.find((item) => item.qid === qid);
                if (!q) return;
                if (q.type === 'checkbox') {
                  defaults[qid] = Array.isArray(answer) ? answer : answer ? [answer] : [];
                } else {
                  defaults[qid] = Array.isArray(answer) ? (answer[0] || '') : (answer || '');
                }
              });
            }
          } catch (err) {
            if (err?.response?.status !== 404) {
              showToast(err?.response?.data?.error || 'Error fetching saved answers', 'error');
            }
          }
        }

        if (!cancelled) setAnswers(defaults);
      } catch (error) {
        if (!cancelled) {
          showToast(error?.response?.data?.error || 'Failed to load questions.', 'error');
        }
      } finally {
        if (!cancelled) setFetchingQuestions(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  // Keep the pointer in range whenever the question set changes.
  useEffect(() => {
    setCurrentIndex(0);
  }, [questions.length]);

  const handleChange = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    setQuestionErrors((prev) => {
      if (!prev[qid]) return prev;
      const next = { ...prev };
      delete next[qid];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!userId) {
      showToast('Could not identify the logged-in user. Please sign in again.', 'error');
      return;
    }

    // Require an answer for every question
    const errors = {};
    questions.forEach((q) => {
      if (!isAnswered(q, answers[q.qid])) errors[q.qid] = true;
    });
    setQuestionErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstUnansweredIndex = questions.findIndex((q) => errors[q.qid]);
      if (firstUnansweredIndex !== -1) setCurrentIndex(firstUnansweredIndex);
      showToast('Please answer all questions before submitting.', 'error');
      return;
    }

    const payload = {
      user_id: userId,
      responses: questions.map((q) => ({
        qid: q.qid,
        label: q.label,
        type: q.type,
        answer: answers[q.qid],
      })),
    };

    try {
      setLoading(true);
      await httpcommon.post('/school/help_us_know_you/submit/', payload);
      onComplete();
    } catch (error) {
      showToast(
        error?.response?.data?.error || 'Something went wrong while saving your answers.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const total = questions.length;
  const question = questions[currentIndex];
  const isLast = currentIndex === total - 1;
  const answeredCount = questions.filter((q) => isAnswered(q, answers[q.qid])).length;
  const pct = total > 0 ? ((currentIndex + 1) / total) * 100 : 0;

  // Progress ring geometry
  const R = 46;
  const CIRC = 2 * Math.PI * R;
  const ringOffset = total > 0 ? CIRC * (1 - answeredCount / total) : CIRC;

  const goBack = () => {
    setCurrentIndex((i) => Math.max(i - 1, 0));
  };

  const jumpTo = (i) => {
    setCurrentIndex(i);
  };

  const goNext = () => {
    if (!question) return;
    if (!isAnswered(question, answers[question.qid])) {
      setQuestionErrors((prev) => ({ ...prev, [question.qid]: true }));
      showToast('Please answer this question to continue.', 'error');
      return;
    }
    if (isLast) {
      handleSubmit();
      return;
    }
    setCurrentIndex((i) => Math.min(i + 1, total - 1));
  };

  const selectRadio = (qid, option) => {
    handleChange(qid, option);
  };

  const toggleCheckbox = (qid, option, max) => {
    const current = Array.isArray(answers[qid]) ? answers[qid] : [];
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    if (!max || next.length <= max) handleChange(qid, next);
  };

  const handleStartOver = () => {
    const blank = {};
    questions.forEach((q) => {
      blank[q.qid] = defaultFor(q);
    });
    setAnswers(blank);
    setQuestionErrors({});
    setCurrentIndex(0);
    setResetOpen(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '22px',
        overflow: 'hidden',
        border: '1px solid #E7EAF1',
        boxShadow: '0 18px 48px rgba(41, 45, 91, 0.08)',
        backgroundColor: '#fff',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
        minHeight: { xs: 'auto', md: 600 },
      }}
    >
      {/* â”€â”€ Left rail â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(160deg, #262B61 0%, #363C7C 55%, #4B3E86 100%)',
          color: '#fff',
          p: { xs: 2.5, md: 3.5 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -70,
            right: -70,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(246,160,58,0.35) 0%, rgba(246,160,58,0) 70%)',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                p: 0,
                width: 30,
                height: 30,
                flexShrink: 0,
                backgroundColor: 'rgba(255,255,255,0.12)',
                borderRadius: '50%',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ArrowBack sx={{ fontSize: 17, color: '#fff' }} />
            </IconButton>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '2px', opacity: 0.7 }}>
              ABOUT YOU
            </Typography>
          </Box>

          <Typography sx={{ fontSize: { xs: 20, md: 23 }, fontWeight: 800, mt: 2, lineHeight: 1.2 }}>
            Help us know you
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.78, mt: 1, lineHeight: 1.6, maxWidth: 260 }}>
            A few quick questions, one at a time. Some let you pick more than one - answer honestly.
          </Typography>
        </Box>

        {/* Progress ring */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            mt: { xs: 3, md: 'auto' },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ position: 'relative', width: 108, height: 108, flexShrink: 0 }}>
            <Box component="svg" viewBox="0 0 108 108" sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              <circle cx="54" cy="54" r={R} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="7" />
              <MotionBox
                component="circle"
                cx="54"
                cy="54"
                r={R}
                fill="none"
                stroke={ORANGE}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={CIRC}
                animate={{ strokeDashoffset: ringOffset }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{answeredCount}</Typography>
              <Typography sx={{ fontSize: 11, opacity: 0.7 }}>of {total}</Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, opacity: 0.9 }}>
              Question {currentIndex + 1} / {total || '-'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, opacity: 0.6, mt: 0.25 }}>
              {answeredCount === total && total > 0 ? 'All done - ready to save' : 'answered so far'}
            </Typography>
          </Box>
        </Box>

        {/* Question dots */}
        {total > 0 && (
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.9, mt: 2.5 }}>
            {questions.map((q, i) => {
              const done = isAnswered(q, answers[q.qid]);
              const active = i === currentIndex;
              return (
                <Box
                  key={q.qid}
                  onClick={() => jumpTo(i)}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '7px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: done ? '#fff' : active ? '#fff' : 'rgba(255,255,255,0.65)',
                    backgroundColor: done ? ORANGE : 'rgba(255,255,255,0.12)',
                    border: active ? '2px solid #fff' : '2px solid transparent',
                    transition: 'all .15s ease',
                    '&:hover': { backgroundColor: done ? ORANGE : 'rgba(255,255,255,0.22)' },
                  }}
                >
                  {i + 1}
                </Box>
              );
            })}
          </Box>
        )}

        {total > 0 && (
          <Button
            onClick={() => setResetOpen(true)}
            disabled={loading || answeredCount === 0}
            startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              position: 'relative',
              zIndex: 1,
              alignSelf: 'flex-start',
              mt: 2.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.85)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
            }}
          >
            Start over
          </Button>
        )}
      </Box>

      {/* â”€â”€ Right panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, backgroundColor: '#FCFCFE' }}>
        {fetchingQuestions ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, py: 8 }}>
            <CircularProgress />
          </Box>
        ) : total === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, py: 8 }}>
            <Typography variant="body2">No questions available.</Typography>
          </Box>
        ) : question ? (
          <>
            {/* Slim progress line */}
            <Box sx={{ height: 4, backgroundColor: '#EEF0F5', flexShrink: 0 }}>
              <MotionBox
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                sx={{ height: '100%', background: 'linear-gradient(90deg, #F6A03A, #F7B65C)' }}
              />
            </Box>

            {/* Question + options (scrolls if long) */}
            <MotionBox
              key={question.qid}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              sx={{
                flex: { xs: 'none', md: 1 },
                minHeight: 0,
                overflowY: { xs: 'visible', md: 'auto' },
                px: { xs: 2.5, md: 4.5 },
                pt: { xs: 3, md: 4 },
                pb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    px: 1,
                    height: 24,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 12,
                    fontWeight: 800,
                    color: ORANGE,
                    backgroundColor: '#FFF4E4',
                  }}
                >
                  Q{currentIndex + 1}
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#9AA0B2', letterSpacing: '0.4px' }}>
                  {question.type === 'checkbox'
                    ? question.max
                      ? `SELECT UP TO ${question.max}`
                      : 'SELECT ALL THAT APPLY'
                    : 'CHOOSE ONE'}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 19, md: 22 },
                  fontWeight: 800,
                  color: NAVY,
                  lineHeight: 1.35,
                  mb: 3,
                  maxWidth: 620,
                }}
              >
                {question.label}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: question.options.length > 5 ? '1fr 1fr' : '1fr' },
                  gap: 1.25,
                  maxWidth: 760,
                }}
              >
                {question.type === 'checkbox'
                  ? question.options.map((option) => {
                      const val = Array.isArray(answers[question.qid]) ? answers[question.qid] : [];
                      return (
                        <OptionRow
                          key={option}
                          multi
                          selected={val.includes(option)}
                          label={toTitleCase(option)}
                          onClick={() => toggleCheckbox(question.qid, option, question.max)}
                        />
                      );
                    })
                  : question.options.map((option) => (
                      <OptionRow
                        key={option}
                        selected={answers[question.qid] === option}
                        label={toTitleCase(option)}
                        onClick={() => selectRadio(question.qid, option)}
                      />
                    ))}
              </Box>

              {questionErrors[question.qid] && (
                <Typography sx={{ color: '#E5484D', fontSize: 13, fontWeight: 600, mt: 2 }}>
                  Please choose an option to continue.
                </Typography>
              )}
            </MotionBox>

            {/* Footer */}
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2.5, md: 4.5 },
                py: 2,
                borderTop: '1px solid #EEF0F5',
                backgroundColor: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Button
                onClick={goBack}
                disabled={currentIndex === 0}
                startIcon={<WestRoundedIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 14,
                  color: '#7A8096',
                  borderRadius: '10px',
                  px: 1.5,
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                Back
              </Button>

              <CommonButtonActionButton
                onClick={goNext}
                disabled={loading}
                endIcon={!isLast ? <EastRoundedIcon /> : undefined}
                sx={{
                  minWidth: 180,
                  borderRadius: '12px',
                  py: 1.1,
                  fontWeight: 700,
                  fontSize: 15,
                  textTransform: 'none',
                  boxShadow: loading ? 'none' : '0 10px 22px rgba(246, 160, 58, 0.32)',
                }}
              >
                {isLast ? (loading ? 'Submitting...' : 'Save & Continue') : 'Next'}
              </CommonButtonActionButton>
            </Box>
          </>
        ) : null}
      </Box>

      {/* Start-over confirmation */}
      <Dialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        PaperProps={{ sx: { borderRadius: '18px', p: 1, maxWidth: 380 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>
          Start these questions over?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#3E4A66', lineHeight: 1.7 }}>
            This clears every answer you&apos;ve picked and takes you back to
            question&nbsp;1. Nothing is saved until you finish, so your earlier
            responses won&apos;t be kept.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setResetOpen(false)}
            sx={{
              borderRadius: '100px',
              textTransform: 'none',
              fontWeight: 700,
              color: '#7A8096',
              px: 2.5,
            }}
          >
            Keep going
          </Button>
          <CommonButtonActionButton
            onClick={handleStartOver}
            sx={{ borderRadius: '100px', minWidth: 150, fontWeight: 700, textTransform: 'none' }}
          >
            Yes, start over
          </CommonButtonActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeneralQuestions;
